import React, { useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { ChevronRight } from 'lucide-react';
import {
  BasicInfoStep,
  LocationStep,
  AddressStep,
  EssentialInfoStep,
  IDUploadStep,
  CompletionStep,
} from './CommonOnboarding';
import {
  createUserDocument,
  createProfessionalDocument,
  validateUserDocument,
  validateProfessionalDocument,
} from '../../../dbSchema';
import InviteStep from './InviteStep';

const BRAND_COLORS = {
  primary: '#1a1a1a',
  accent: '#C9A961',
  light: '#F5F5F0',
  lightGray: '#E8E8E3',
};

const ProfessionalOnboarding = ({ user, onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form data
  const [displayName, setDisplayName] = useState('');
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState({
    addressLine: '',
    landmark: '',
    city: '',
    state: '',
    pincode: '',
    country: 'IN'
  });
  const [essentialInfo, setEssentialInfo] = useState({
    dateOfBirth: '',
    age: null,
    sex: '',
    aadhaarLinkedMobile: '',
    employmentStatus: '',
    currentCourse: '',
    highestEducation: '',
    hasWorkedBefore: null,
  });
  const [skills, setSkills] = useState('');
  const [skillSummary, setSkillSummary] = useState('');
  const [languages, setLanguages] = useState('English');
  const [isAvailableNow, setIsAvailableNow] = useState(false);
  const [idFile, setIdFile] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);

  const [inviteData, setInviteData] = useState({
    inviteCodeUsed: null,
    invitedBy: null,
    skipped: false
  });

  const totalSteps = 9; // Added profile picture step (Step 8 is now Profile Picture, 9 is Completion)

  const handleNext = async () => {
    setError('');

    // Validation
    // New Invite Step is Step 1
    if (step === 1) {
      // Handled by component logic
    }

    if (step === 2 && !displayName) {
      setError('Please enter your name');
      return;
    }

    if (step === 3 && !location) {
      setError('Please capture your location');
      return;
    }

    if (step === 4 && (!address.addressLine || !address.city || !address.pincode)) {
      setError('Please complete all required address fields');
      return;
    }

    if (step === 5) {
      if (!essentialInfo.dateOfBirth || !essentialInfo.age) {
        setError('Please enter a valid date of birth');
        return;
      }
      if (!essentialInfo.sex) {
        setError('Please select your sex');
        return;
      }
      if (!essentialInfo.aadhaarLinkedMobile || essentialInfo.aadhaarLinkedMobile.length !== 10) {
        setError('Please enter a valid 10-digit Aadhaar linked mobile number');
        return;
      }
      if (!essentialInfo.employmentStatus) {
        setError('Please select your employment status');
        return;
      }
      if (essentialInfo.employmentStatus === 'Student' && !essentialInfo.currentCourse) {
        setError('Please enter your current course');
        return;
      }
      if (!essentialInfo.highestEducation) {
        setError('Please select your highest educational qualification');
        return;
      }
      if (essentialInfo.hasWorkedBefore === null) {
        setError('Please indicate if you have worked before');
        return;
      }
    }

    if (step === 6 && (!skills || !skillSummary)) {
      setError('Please complete all required fields');
      return;
    }

    if (step === 7 && !idFile) {
      setError('Please upload your ID document');
      return;
    }

    // Step 8 is Profile Picture (optional)

    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      await handleComplete();
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    setError('');

    try {
      // Upload ID document
      const fileExtension = idFile.name.split('.').pop();
      const fileName = `id_document_1.${fileExtension}`;
      const idStoragePath = `user-ids/${user.uid}/${fileName}`;
      const storageRef = ref(storage, idStoragePath);
      await uploadBytes(storageRef, idFile);

      // Upload Profile Picture if exists
      let profilePictureData = null;
      if (profilePicture) {
        const ppExtension = profilePicture.name.split('.').pop();
        const ppFileName = `profile_picture.${ppExtension}`;
        const ppStoragePath = `user-profiles/${user.uid}/${ppFileName}`;
        const ppRef = ref(storage, ppStoragePath);

        await uploadBytes(ppRef, profilePicture);

        const { getDownloadURL } = await import('firebase/storage');
        const ppUrl = await getDownloadURL(ppRef);

        profilePictureData = {
          storagePath: ppStoragePath,
          url: ppUrl
        };
      }

      // Create base user document using schema
      const userData = createUserDocument({
        uid: user.uid,
        phoneNumber: user.phoneNumber,
        displayName,
        role: 'professional',
        idStoragePath,
        invitedBy: inviteData.invitedBy,
        inviteCodeUsed: inviteData.inviteCodeUsed,
      });

      // Validate user document using schema
      const userValidation = validateUserDocument(userData);
      if (!userValidation.isValid) {
        throw new Error(`User validation failed: ${userValidation.errors.join(', ')}`);
      }

      // Create batch
      const { writeBatch } = await import('firebase/firestore');
      const batch = writeBatch(db);

      const userRef = doc(db, 'users', user.uid);
      batch.set(userRef, userData);

      // Create professional document using schema
      const professionalData = createProfessionalDocument({
        uid: user.uid,
        displayName,
        address,
        location,
        dateOfBirth: essentialInfo.dateOfBirth,
        age: essentialInfo.age,
        sex: essentialInfo.sex,
        aadhaarLinkedMobile: essentialInfo.aadhaarLinkedMobile,
        employmentStatus: essentialInfo.employmentStatus,
        currentCourse: essentialInfo.currentCourse,
        highestEducation: essentialInfo.highestEducation,
        hasWorkedBefore: essentialInfo.hasWorkedBefore,
        skills: skills.split(',').map(s => s.trim()).filter(Boolean),
        skillSummary,
        languages: languages.split(',').map(l => l.trim()).filter(Boolean),
        isAvailableNow,
        phoneNumber: user.phoneNumber,
        idStoragePath,
        invitedBy: inviteData.invitedBy,
        inviteCodeUsed: inviteData.inviteCodeUsed,
        profilePicture: profilePictureData,
      });

      // Validate professional document using schema
      const profValidation = validateProfessionalDocument(professionalData);
      if (!profValidation.isValid) {
        throw new Error(`Professional validation failed: ${profValidation.errors.join(', ')}`);
      }

      const profRef = doc(db, 'professionals', user.uid);
      batch.set(profRef, professionalData);

      // Commit batch
      await batch.commit();

      onComplete();
    } catch (err) {
      console.error('Error completing onboarding:', err);
      setError(err.message || 'Failed to complete onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12" style={{ backgroundColor: BRAND_COLORS.light }}>
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Progress Bar */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-light tracking-widest" style={{ color: BRAND_COLORS.primary }}>
              STEP {step} OF {totalSteps}
            </p>
            <p className="text-xs font-light tracking-wider text-gray-500">
              {Math.round((step / totalSteps) * 100)}% COMPLETE
            </p>
          </div>
          <div className="w-full h-1 bg-gray-200">
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${(step / totalSteps) * 100}%`,
                backgroundColor: BRAND_COLORS.accent
              }}
            ></div>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white border-2 p-8 md:p-10" style={{ borderColor: BRAND_COLORS.primary }}>
          {error && (
            <div className="mb-6 p-4 border-2 border-red-600 bg-red-50">
              <p className="text-red-700 text-sm font-light">{error}</p>
            </div>
          )}

          {step === 1 && (
            <InviteStep
              setInviteData={setInviteData}
              handleNext={() => setStep(2)}
              setError={setError}
              role="professional"
            />
          )}

          {step === 2 && (
            <BasicInfoStep
              displayName={displayName}
              setDisplayName={setDisplayName}
              role="professional"
            />
          )}

          {step === 3 && (
            <LocationStep
              location={location}
              setLocation={setLocation}
              loading={loading}
              setLoading={setLoading}
              setError={setError}
            />
          )}

          {step === 4 && (
            <AddressStep address={address} setAddress={setAddress} />
          )}

          {step === 5 && (
            <EssentialInfoStep
              essentialInfo={essentialInfo}
              setEssentialInfo={setEssentialInfo}
              setError={setError}
            />
          )}

          {step === 6 && (
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-light tracking-wide mb-3" style={{ color: BRAND_COLORS.primary }}>
                  Your Interests and Skills
                </h3>
                <p className="text-gray-600 font-light">Tell us about your expertise</p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
                    Interests
                  </label>

                  {/* Tags Container */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {[
                      "Sales",
                      "Greeting",
                      "Ops and Admin",
                      "IT Support",
                      "Data Entry",
                      "Inventory Management",
                      "Front Office Work"
                    ].map((skill) => {
                      const isSelected = skills.split(',').map(s => s.trim()).includes(skill);
                      return (
                        <button
                          key={skill}
                          type="button"
                          onClick={() => {
                            const currentSkills = skills.split(',').map(s => s.trim()).filter(Boolean);
                            let newSkills;
                            if (currentSkills.includes(skill)) {
                              newSkills = currentSkills.filter(s => s !== skill);
                            } else {
                              newSkills = [...currentSkills, skill];
                            }
                            setSkills(newSkills.join(', '));
                          }}
                          className={`px-3 py-2 border rounded-full text-xs font-light transition-all duration-300 ${isSelected
                            ? 'bg-black border-black text-white'
                            : 'bg-transparent border-gray-300 text-gray-600 hover:border-black'
                            }`}
                        >
                          {skill}
                        </button>
                      );
                    })}
                  </div>

                  <input
                    type="text"
                    value={skills}
                    onChange={(e) => setSkills(e.target.value)}
                    placeholder=""
                    className="w-full px-4 py-3 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black"
                    style={{ borderColor: BRAND_COLORS.lightGray }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
                    Brief description about you and your interests
                  </label>
                  <textarea
                    value={skillSummary}
                    onChange={(e) => setSkillSummary(e.target.value)}
                    placeholder=""
                    rows={4}
                    className="w-full px-4 py-3 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black resize-none"
                    style={{ borderColor: BRAND_COLORS.lightGray }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-light tracking-widest mb-3 uppercase" style={{ color: BRAND_COLORS.primary }}>
                    Languages (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={languages}
                    onChange={(e) => setLanguages(e.target.value)}
                    placeholder="e.g., English, Hindi"
                    className="w-full px-4 py-3 border-2 bg-transparent font-light transition-all duration-300 focus:outline-none focus:border-black"
                    style={{ borderColor: BRAND_COLORS.lightGray }}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 7 && (
            <IDUploadStep idFile={idFile} setIdFile={setIdFile} setError={setError} />
          )}

          {step === 8 && (
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-light tracking-wide mb-3" style={{ color: BRAND_COLORS.primary }}>
                  Profile Picture
                </h3>
                <p className="text-gray-600 font-light">Add a photo to help others recognize you</p>
              </div>

              <div className="flex flex-col items-center justify-center py-8">
                <label className="cursor-pointer relative">
                  <div
                    className="w-40 h-40 rounded-full items-center justify-center border-2 border-dashed overflow-hidden bg-gray-50"
                    style={{ borderColor: BRAND_COLORS.lightGray }}
                  >
                    {profilePicture ? (
                      <img
                        src={URL.createObjectURL(profilePicture)}
                        alt="Profile Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" /><circle cx="12" cy="13" r="3" /></svg>
                      </div>
                    )}
                  </div>

                  <div className="absolute bottom-0 right-0 bg-black p-3 rounded-full border-4 border-white">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                  </div>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file && file.size <= 5 * 1024 * 1024) {
                        setProfilePicture(file);
                        setError('');
                      } else {
                        setError('File size must be less than 5MB');
                      }
                    }}
                    className="hidden"
                  />
                </label>

                <p className="mt-6 text-sm text-gray-500 font-normal text-center">
                  {profilePicture ? 'Click to change photo' : 'Click to upload photo'}
                </p>
              </div>
            </div>
          )}

          {step === 9 && <CompletionStep />}

          {/* Navigation Buttons */}
          <div className="mt-10 flex items-center justify-between">
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="px-6 py-3 border-2 font-light tracking-wide transition-all duration-300 hover:border-black"
                style={{ borderColor: BRAND_COLORS.lightGray }}
              >
                BACK
              </button>
            )}
            {step > 1 && (
              <button
                type="button"
                onClick={handleNext}
                disabled={loading}
                className="ml-auto py-4 px-8 bg-black text-white font-light tracking-widest text-sm transition-all duration-300 hover:bg-opacity-90 flex items-center space-x-3"
              >
                <span>{loading ? 'PROCESSING...' : (step === totalSteps ? 'COMPLETE' : 'CONTINUE')}</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalOnboarding;
